import React, { useRef, useState, useEffect, useMemo, forwardRef } from "react";
import {
  AnimatePresence,
  motion,
  Reorder,
  useDragControls,
} from "framer-motion";
import { Plus, Calendar, CheckCircle, Circle, Trash2 } from "lucide-react";

// Import date picker
import DatePicker from "react-datepicker";

const todoData = window.todoData || { todos: [], lists: [] };

/* -------------------------- Inject datepicker CSS -------------------------- */
let __datepickerStylesInjected = false;
function injectDatepickerStylesOnce() {
  if (__datepickerStylesInjected) return;
  __datepickerStylesInjected = true;
  const css = `
  .react-datepicker {
    font-family: ui-sans-serif, system-ui, -apple-system, "Segoe UI", Roboto, Ubuntu, "Helvetica Neue", Arial, "Noto Sans", "Apple Color Emoji", "Segoe UI Emoji";
    font-size: 12px;
    background: #fff;
    border: 1px solid rgba(0,0,0,0.10);
    border-radius: 10px;
    box-shadow: 0 10px 24px rgba(0,0,0,0.10);
    color: rgba(0,0,0,0.85);
    overflow: hidden;
    min-width: 232px;
    transform: translateX(calc(-100% + 32px));
  }
    .react-datepicker__aria-live { display: none; }
    .react-datepicker__month-container { float: none; }
    .react-datepicker__header {
    background: #fff;
    border-bottom: 1px solid rgba(0,0,0,0.08);
    padding: 0;
    text-align: center;
  }
    .react-datepicker__current-month, .react-datepicker-time__header, .react-datepicker-year-header {
    font-size: 12px;
    font-weight: 600;
    color: rgba(0,0,0,0.75);
  }
  .react-datepicker__day-names {
    display: flex;
    justify-content: center;
    gap: 0.2rem;
    margin: 0.2rem 0 0.1rem 0;
  }
  .react-datepicker__day-name, .react-datepicker__day, .react-datepicker__time-name {
    width: 2rem;
    line-height: 2rem;
    text-align: center;
    margin: 0.1rem;
    color: rgba(0,0,0,0.70);
  }
  .react-datepicker__day-name { color: rgba(0,0,0,0.45); font-weight: 600; }
  .react-datepicker__month { margin: 0.25rem 0.4rem 0.5rem; }
  .react-datepicker__week {
    display: flex;
    justify-content: center;
  }
  .react-datepicker__day:hover {
    background: rgba(0,0,0,0.06);
    border-radius: 6px;
    cursor:pointer;
  }
  .react-datepicker__day--selected, .react-datepicker__day--keyboard-selected {
    background: rgba(0,0,0,0.85);
    color: #fff;
    border-radius: 6px;
  }
  .react-datepicker__navigation { display: none !important; }
  .react-datepicker__triangle { display: none; }
  .react-datepicker-popper {
    position: absolute !important;
    z-index: 70;
  }
  `;
  const style = document.createElement("style");
  style.setAttribute("data-injected", "react-datepicker-minimal");
  style.textContent = css;
  document.head.appendChild(style);
}

/* Framer-motion wrappers */
const MotionCard = motion.div;

/* --------------------------------- Utils -------------------------------- */

function uid() {
  if (typeof crypto !== "undefined" && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  return (
    (Date.now().toString(36) + Math.random().toString(36).slice(2, 10)).toUpperCase()
  );
}

function formatDueDate(d) {
  if (!d) return "";
  const parts = d.split("-");
  if (parts.length !== 3) return "";
  const y = parseInt(parts[0], 10);
  const m = parseInt(parts[1], 10);
  const day = parseInt(parts[2], 10);
  const dt = new Date(y, (m || 1) - 1, day || 1);
  const now = new Date();
  const sameYear = dt.getFullYear() === now.getFullYear();
  const opts = sameYear
    ? { month: "short", day: "numeric" }
    : { month: "short", day: "numeric", year: "numeric" };
  return dt.toLocaleDateString(undefined, opts);
}

function parseYMD(s) {
  if (!s || typeof s !== "string") return null;
  const [y, m, d] = s.split("-").map((v) => parseInt(v, 10));
  if (!y || !m || !d) return null;
  return new Date(y, m - 1, d);
}

function toYMD(date) {
  if (!(date instanceof Date) || isNaN(date)) return null;
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

function useClickOutside(ref, handler) {
  useEffect(() => {
    function handleClickOutside(event) {
      if (ref.current && !ref.current.contains(event.target)) handler();
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [ref, handler]);
}

/* -------------------------- Todo Item Component --------------------------- */
function TodoItem({ item, updateItem, deleteItem, onToggleComplete }) {
  const [isHovered, setIsHovered] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [isEditing]);

  const dateLabel = formatDueDate(item.dueDate);
  const selectedDate = parseYMD(item.dueDate);

  const handleTitleClick = () => {
    setIsEditing(true);
  };

  const handleTitleSubmit = () => {
    const newTitle = inputRef.current?.value.trim();
    if (newTitle && newTitle !== item.title) {
      updateItem(item.id, { title: newTitle });
    }
    setIsEditing(false);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      handleTitleSubmit();
    } else if (e.key === 'Escape') {
      setIsEditing(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      className="flex items-center gap-3 p-3 bg-white rounded-lg border border-gray-200 mb-2 group"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {/* Checkbox */}
      <button
        onClick={() => onToggleComplete(item.id)}
        className="flex-shrink-0 w-5 h-5 rounded-full border-2 border-gray-300 flex items-center justify-center hover:border-blue-500 transition-colors"
      >
        {item.isComplete && (
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            className="w-2 h-2 bg-blue-500 rounded-full"
          />
        )}
      </button>

      {/* Title */}
      <div className="flex-1 min-w-0">
        {isEditing ? (
          <input
            ref={inputRef}
            defaultValue={item.title}
            onBlur={handleTitleSubmit}
            onKeyDown={handleKeyDown}
            className="w-full bg-transparent border-none outline-none focus:ring-2 focus:ring-blue-500 rounded px-1"
          />
        ) : (
          <div
            onClick={handleTitleClick}
            className={`cursor-text ${item.isComplete ? 'line-through text-gray-500' : 'text-gray-900'}`}
          >
            {item.title || 'Untitled todo'}
          </div>
        )}
      </div>

      {/* Due date */}
      {dateLabel && (
        <div className="text-sm text-gray-500 flex-shrink-0">
          {dateLabel}
        </div>
      )}

      {/* Delete button */}
      <motion.button
        initial={{ opacity: 0, scale: 0.8 }}
        animate={{ opacity: isHovered ? 1 : 0, scale: isHovered ? 1 : 0.8 }}
        className="flex-shrink-0 w-6 h-6 rounded-full bg-red-100 text-red-600 hover:bg-red-200 flex items-center justify-center"
        onClick={() => deleteItem(item.id)}
      >
        <Trash2 className="w-3 h-3" />
      </motion.button>
    </motion.div>
  );
}

/* ================================ Main App =================================== */
export function App() {
  const [todos, setTodos] = useState(todoData.todos || []);
  const [newTodoTitle, setNewTodoTitle] = useState('');

  useEffect(() => {
    injectDatepickerStylesOnce();
  }, []);

  const addTodo = () => {
    if (!newTodoTitle.trim()) return;

    const newTodo = {
      id: uid(),
      title: newTodoTitle.trim(),
      isComplete: false,
      note: '',
      dueDate: null,
      createdAt: new Date().toISOString()
    };

    setTodos(prev => [newTodo, ...prev]);
    setNewTodoTitle('');
  };

  const updateTodo = (id, updates) => {
    setTodos(prev => prev.map(todo =>
      todo.id === id ? { ...todo, ...updates } : todo
    ));
  };

  const deleteTodo = (id) => {
    setTodos(prev => prev.filter(todo => todo.id !== id));
  };

  const toggleComplete = (id) => {
    setTodos(prev => prev.map(todo =>
      todo.id === id ? { ...todo, isComplete: !todo.isComplete } : todo
    ));
  };

  const completedCount = todos.filter(todo => todo.isComplete).length;
  const totalCount = todos.length;

  return (
    <div className="w-full max-w-md mx-auto bg-gray-50 min-h-screen p-4">
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-xl font-semibold text-gray-900 mb-2">My Todos</h1>
          <p className="text-sm text-gray-600">
            {completedCount} of {totalCount} completed
          </p>
        </div>

        {/* Add new todo */}
        <div className="flex gap-2 mb-4">
          <input
            type="text"
            value={newTodoTitle}
            onChange={(e) => setNewTodoTitle(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && addTodo()}
            placeholder="Add a new todo..."
            className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
          <button
            onClick={addTodo}
            disabled={!newTodoTitle.trim()}
            className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
          >
            <Plus className="w-4 h-4" />
            Add
          </button>
        </div>

        {/* Todo list */}
        <AnimatePresence>
          {todos.length === 0 ? (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="text-center py-8 text-gray-500"
            >
              No todos yet. Add one above to get started!
            </motion.div>
          ) : (
            <div className="space-y-2">
              {todos.map((todo) => (
                <TodoItem
                  key={todo.id}
                  item={todo}
                  updateItem={updateTodo}
                  deleteItem={deleteTodo}
                  onToggleComplete={toggleComplete}
                />
              ))}
            </div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}

export default App;

// Auto-mount if running in browser
if (typeof window !== 'undefined') {
  import('react-dom/client').then(({ createRoot }) => {
    const rootElement = document.getElementById('todo-chatgpt-app-root') || document.getElementById('todo-root');
    if (rootElement) {
      const root = createRoot(rootElement);
      root.render(React.createElement(App));
    }
  });
}
